import React from "react";
import {
  Box,
  Button,
  DialogContent,
  FormHelperText,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { DEFAULT_INTENSITY_OPTIONS } from "./listeningFormUtils";
import {
  getListeningFieldError,
  hasListeningFieldError,
} from "./listeningValidation";
import { LISTENING_FORM_STEPS } from "./listeningModes";
import MultilineTextField from "../shared/MultilineTextField";
import { parseListeningClipboard } from "./listeningClipboard";

const LISTENING_TITLE_MAX_LENGTH = 80;
const LISTENING_LOCATION_MAX_LENGTH = 128;
const LISTENING_DESCRIPTION_MAX_LENGTH = 2048;

function SelectionButtons({
  label,
  options,
  selected,
  onToggle,
  suffix = "",
  disabled = false,
}) {
  return (
    <Box mb={2}>
      <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
        {label}
      </Typography>

      <Box display="flex" flexWrap="wrap">
        {options.map((option) => {
          const isSelected = selected.includes(option);

          return (
            <Button
              key={option}
              variant={isSelected ? "contained" : "outlined"}
              color={isSelected ? "primary" : "default"}
              onClick={() => onToggle(option)}
              style={{
                marginRight: 8,
                marginBottom: 8,
                minWidth: 72,
                textTransform: "none",
              }}
              disabled={disabled}
            >
              {option}
              {suffix}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}

function ListeningDeclarationStep({
  form,
  errors,
  isByModel,
  availableFrequencies,
  onFieldChange,
  onToggleFrequency,
  onToggleIntensity,
  showDeviceIdField = true,
}) {
  const titleLength = form.title?.length || 0;
  const descriptionLength = form.description?.length || 0;
  const locationLength = form.location?.length || 0;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          label="Tytuł"
          value={form.title}
          onChange={(event) => onFieldChange("title", event.target.value)}
          fullWidth
          variant="outlined"
          inputProps={{ maxLength: LISTENING_TITLE_MAX_LENGTH }}
          helperText={`Domyślnie: ${form.defaultGeneratedTitle} • ${titleLength}/${LISTENING_TITLE_MAX_LENGTH}`}
        />
      </Grid>

      <Grid item xs={12}>
        <MultilineTextField
          label="Opis"
          value={form.description}
          onValueChange={(value) => onFieldChange("description", value)}
          rows={7}
          rowsMax={14}
          required
          variant="outlined"
          inputProps={{ maxLength: LISTENING_DESCRIPTION_MAX_LENGTH }}
          error={Boolean(errors.description)}
          helperText={
            errors.description ||
            `${descriptionLength}/${LISTENING_DESCRIPTION_MAX_LENGTH}`
          }
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Lokalizacja"
          value={form.location}
          onChange={(event) => onFieldChange("location", event.target.value)}
          fullWidth
          required
          variant="outlined"
          inputProps={{ maxLength: LISTENING_LOCATION_MAX_LENGTH }}
          error={Boolean(errors.location)}
          helperText={
            errors.location ||
            `${locationLength}/${LISTENING_LOCATION_MAX_LENGTH}`
          }
        />
      </Grid>

      {showDeviceIdField && (
        <Grid item xs={12} md={6}>
          <TextField
            label="Device ID"
            value={form.deviceId}
            onChange={(event) => onFieldChange("deviceId", event.target.value)}
            fullWidth
            required={isByModel}
            variant="outlined"
            error={Boolean(errors.deviceId)}
            helperText={
              isByModel ? errors.deviceId || "Required for BY_MODEL." : ""
            }
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <SelectionButtons
          label="Testowane częstotliwości"
          options={availableFrequencies}
          selected={form.selectedFrequencies}
          onToggle={onToggleFrequency}
        />
        {errors.selectedFrequencies && (
          <FormHelperText error>{errors.selectedFrequencies}</FormHelperText>
        )}
      </Grid>

      <Grid item xs={12}>
        <SelectionButtons
          label="Testowane intensywności"
          options={DEFAULT_INTENSITY_OPTIONS}
          selected={form.selectedIntensities}
          onToggle={onToggleIntensity}
          suffix=" dB"
        />
        {errors.selectedIntensities && (
          <FormHelperText error>{errors.selectedIntensities}</FormHelperText>
        )}
      </Grid>
    </Grid>
  );
}

function ListeningValuesStep({
  form,
  errors,
  fieldErrors,
  onFieldChange,
  onNoStimulusChange,
  onTableValueChange,
}) {
  const [pasteError, setPasteError] = React.useState("");

  const handleTablePaste = (event, selectedFrequency, selectedIntensity) => {
    event.preventDefault();
    const parsed = parseListeningClipboard(
      event.clipboardData.getData("text"),
      form.selectedFrequencies.length,
      form.selectedIntensities.length,
    );

    if (parsed.error) {
      setPasteError(parsed.error);
      return;
    }

    setPasteError("");
    if (parsed.kind === "single") {
      onTableValueChange(
        selectedFrequency,
        selectedIntensity,
        parsed.value,
      );
      return;
    }

    form.selectedFrequencies.forEach((frequency, rowIndex) => {
      form.selectedIntensities.forEach((intensity, columnIndex) => {
        onTableValueChange(
          frequency,
          intensity,
          parsed.values[rowIndex][columnIndex],
        );
      });
    });
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Ogólny poziom tła [dB]"
            type="number"
            inputProps={{ step: 0.01 }}
            value={form.generalBackgroundIntensity}
            onChange={(event) =>
              onFieldChange("generalBackgroundIntensity", event.target.value)
            }
            fullWidth
            required
            variant="outlined"
            error={Boolean(errors.generalBackgroundIntensity)}
            helperText={errors.generalBackgroundIntensity}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Błąd sonometru [dB]"
            type="number"
            inputProps={{ step: 0.01 }}
            value={form.sonometerError}
            onChange={(event) =>
              onFieldChange("sonometerError", event.target.value)
            }
            fullWidth
            required
            variant="outlined"
            error={Boolean(errors.sonometerError)}
            helperText={errors.sonometerError}
          />
        </Grid>
      </Grid>

      <Box mt={3}>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Bez bodźca [dB]</TableCell>
                <TableCell>Częstotliwość</TableCell>
                {form.selectedIntensities.map((intensity) => (
                  <TableCell key={intensity} align="center">
                    {intensity}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {form.selectedFrequencies.map((frequency) => (
                <TableRow key={frequency}>
                  <TableCell style={{ minWidth: 160 }}>
                    <TextField
                      type="number"
                      inputProps={{ step: 0.01 }}
                      value={form.noStimulusByFrequency[frequency] ?? ""}
                      onChange={(event) =>
                        onNoStimulusChange(frequency, event.target.value)
                      }
                      variant="outlined"
                      size="small"
                      error={hasListeningFieldError(
                        fieldErrors,
                        `noStimulusByFrequency.${frequency}`,
                      )}
                      helperText={getListeningFieldError(
                        fieldErrors,
                        `noStimulusByFrequency.${frequency}`,
                      )}
                      fullWidth
                    />
                  </TableCell>

                  <TableCell>{frequency} Hz</TableCell>

                  {form.selectedIntensities.map((intensity) => (
                    <TableCell key={`${frequency}-${intensity}`}>
                      <TextField
                        type="number"
                        inputProps={{ step: 0.01 }}
                        value={form.tableValues?.[frequency]?.[intensity] ?? ""}
                        onChange={(event) =>
                          onTableValueChange(
                            frequency,
                            intensity,
                            event.target.value,
                          )
                        }
                        onPaste={(event) =>
                          handleTablePaste(event, frequency, intensity)
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={hasListeningFieldError(
                          fieldErrors,
                          `tableValues.${frequency}.${intensity}`,
                        )}
                        helperText={getListeningFieldError(
                          fieldErrors,
                          `tableValues.${frequency}.${intensity}`,
                        )}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {errors.noStimulusByFrequency && (
          <FormHelperText error style={{ marginTop: 8 }}>
            {errors.noStimulusByFrequency}
          </FormHelperText>
        )}

        {errors.tableValues && (
          <FormHelperText error style={{ marginTop: 8 }}>
            {errors.tableValues}
          </FormHelperText>
        )}

        {pasteError && (
          <FormHelperText error style={{ marginTop: 8 }}>
            {pasteError}
          </FormHelperText>
        )}
      </Box>
    </>
  );
}

export default function ListeningFormView({
  step,
  modeLabel,
  modeIcon,
  form,
  validationErrors,
  fieldErrors,
  availableFrequencies,
  onFieldChange,
  onToggleFrequency,
  onToggleIntensity,
  onNoStimulusChange,
  onTableValueChange,
  showDeviceIdField,
  isByModel,
  submitError,
}) {
  const ModeIcon = modeIcon;

  return (
    <DialogContent dividers>
      <Box mb={2} display="flex" alignItems="center">
        <ModeIcon style={{ marginRight: 8 }} />
        <Typography variant="subtitle2">{modeLabel}</Typography>
      </Box>

      {step === LISTENING_FORM_STEPS.DECLARATION ? (
        <ListeningDeclarationStep
          form={form}
          errors={validationErrors}
          isByModel={isByModel}
          availableFrequencies={availableFrequencies}
          onFieldChange={onFieldChange}
          onToggleFrequency={onToggleFrequency}
          onToggleIntensity={onToggleIntensity}
          showDeviceIdField={showDeviceIdField}
        />
      ) : (
        <ListeningValuesStep
          form={form}
          errors={validationErrors}
          fieldErrors={fieldErrors}
          onFieldChange={onFieldChange}
          onNoStimulusChange={onNoStimulusChange}
          onTableValueChange={onTableValueChange}
        />
      )}

      {submitError && (
        <Typography color="error" style={{ marginTop: 16 }}>
          {submitError}
        </Typography>
      )}
    </DialogContent>
  );
}
